import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import {
  FontAwesomeModule,
  FaIconLibrary,
} from '@fortawesome/angular-fontawesome';

import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

import { JwtHelperService, JWT_OPTIONS } from '@auth0/angular-jwt';
import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { HomeComponent } from './components/home/home.component';
import { PayMethodsComponent } from './components/pay-methods/pay-methods.component';
import { CreditCardComponent, IframeDialog } from './components/credit-card/credit-card.component';
import { PseComponent } from './components/pse/pse.component';
import { TransactionComponent } from './shared/components/transaction/transaction.component';
import { CashComponent } from './components/cash/cash.component';
import { DepositComponent } from './components/deposit/deposit.component';
import { ButtonsBarComponent } from './shared/components/buttons-bar/buttons-bar.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { PseAvComponent } from './components/pse-av/pse-av.component';
import { WalletComponent } from './components/wallet/wallet.component';
import { FooterComponent } from './components/footer/footer.component';
import { SharedModule } from './shared/shared.module';
import { ReturnMethodsComponent } from './shared/components/return-methods/return-methods.component';
import { ReturnComponent } from './components/return/return.component';
import { ErrorComponent } from './components/error/error.component';
import { AlertpseComponent } from './components/alertpse/alertpse.component';
import { BuyerInformationComponent } from './components/buyer-information/buyer-information.component';
import { EqualValidator } from './shared/directives/equal-validator.directive';
import { MerchantLogoComponent } from './components/merchant-logo/merchant-logo.component';
import { SecureComponent } from './components/secure/secure.component';
import { HeaderTitleComponent } from './components/header-title/header-title.component';
import { PipesModule } from './pipes/pipes.module';
import { ClipboardModule } from 'ngx-clipboard';
import { CopyToClipboardComponent } from './shared/components/copy-to-clipboard/copy-to-clipboard.component';
import { SelectionButtonComponent } from './components/selection-button/selection-button.component';
import { CounterItemComponent } from './components/counter-item/counter-item.component';
import { EfectivoComponent } from './components/efectivo/efectivo.component';
import { TransferInstructionsComponent } from './components/transfer-instructions/transfer-instructions.component';
import { PaymentExpirationComponent } from './components/payment-expiration/payment-expiration.component';
import { MaterialModule } from './material.module';
import { NgxPopperModule } from 'ngx-popper';
import { TransferComponent } from './components/transfer/transfer.component';
import { CreditCardDirectivesModule } from 'angular-cc-library';
import { ErrorStateMatcher } from '@angular/material/core';
import { CustomErrorStateMatcher } from './core/providers/CustomErrorStateMatcher';
import { CashRobotComponent } from './components/cash-robot/cash-robot.component';
import { CoreModule } from './core/core.module';
import { RecaptchaModule } from 'ng-recaptcha';
import { MoneysrcPolicyComponent } from './components/moneysrc-policy/moneysrc-policy.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TransferBamComponent } from './components/transfer-bam/transfer-bam.component';
import { BnplComponent } from './components/bnpl/bnpl.component';
import { YapePeruComponent } from './components/yape-peru/yape-peru.component';
import { PseWalletComponent } from './components/pse-wallet/pse-wallet.component';
import { WalletClaroPayComponent } from './components/wallet-claro-pay/wallet-claro-pay.component';
import { CheckOrderStatusComponent } from './components/wallet-claro-pay/check-order-status/check-order-status.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { UserFormComponent } from './components/user-form/user-form.component';
import { BancolombiaComponent } from './components/bnpl/pages/bancolombia/bancolombia-bnpl.component';
import { QrComponent } from './components/qr/qr.component';
import { MatListModule } from '@angular/material/list';
import { SubMenuComponent } from './components/sub-menu/sub-menu.component';
import { PaymentFromWebsiteComponent } from './components/payment-from-website/payment-from-website.component';
import { LoaderBnplComponent } from './components/bnpl/pages/bancolombia/loader-bnpl/loader-bnpl.component';
import { PeruPaymentServicesComponent } from './components/peru-payment-services/peru-payment-services.component';
import { SplitPaymentComponent } from './components/split-payment/split-payment.component';
import { DecimalPipe } from '@angular/common';
import { InfoOrderLinkComponent } from './components/split-payment/info-order-link/info-order-link.component';
import { NativeDaviplataComponent } from './components/native-daviplata/native-daviplata.component';
import { VoucherTemplateComponent } from './components/native-daviplata/voucher-template/voucher-template.component';
import { SinpeMovilComponent } from './components/sinpe-movil/sinpe-movil.component';
import { ConsumerCreditComponent } from './components/bancolombia/consumer-credit/consumer-credit.component';
import { AmountToLowAlertComponent } from './components/bancolombia/consumer-credit/amount-to-low-alert/amount-to-low-alert.component';
import { SubMenuComponentGU } from './components/sub-menu-gu/sub-menu-gu.component';
import { DeunaEcuadorComponent } from './components/deuna-ecuador/deuna-ecuador.component';
import { PuntosColombiaComponent } from './components/puntos-colombia/puntos-colombia.component';
import { PopupRedirectComponent } from './components/popup-redirect/popup-redirect.component';
import { EmbebedCreditCardComponent } from './components/embebed-credit-card/credit-card.component';
import { WalletDialogComponent } from './components/wallet/wallet-dialog/wallet-dialog.component';
import { TransfiyaComponent } from './components/embebed-transfiya/transfiya.component';
import { EmbebedApiPayInComponent } from './components/embebed-api-pay-in/api-pay-in.component';
import { TransfiyaVoucherComponent } from './components/embebed-transfiya/transfiya-voucher/transfiya-voucher.component';
import { ApiPayInVoucherComponent } from './components/embebed-api-pay-in/embebed-api-pay-in-voucher/api-pay-in-voucher.component';
import { WhiteBrandVoucherComponent } from './components/white-brand-voucher/white-brand-voucher.component';
import { environment } from 'src/environments/environment';
import { PayvalidaFeatureFlagService } from './services/payvalida-feature-flag.service';
import { FLAGS_NAME } from './core/constants/General-constants';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    PayMethodsComponent,
    CreditCardComponent,
    EmbebedCreditCardComponent,
    EmbebedApiPayInComponent,
    PseComponent,
    TransactionComponent,
    CashComponent,
    DepositComponent,
    NavbarComponent,
    PseAvComponent,
    WalletComponent,
    WalletDialogComponent,
    FooterComponent,
    ReturnMethodsComponent,
    ReturnComponent,
    ButtonsBarComponent,
    ErrorComponent,
    AlertpseComponent,
    BuyerInformationComponent,
    EqualValidator,
    MerchantLogoComponent,
    SecureComponent,
    HeaderTitleComponent,
    CopyToClipboardComponent,
    SelectionButtonComponent,
    CounterItemComponent,
    EfectivoComponent,
    TransferInstructionsComponent,
    PaymentExpirationComponent,
    TransferComponent,
    CashRobotComponent,
    MoneysrcPolicyComponent,
    IframeDialog,
    TransferBamComponent,
    BnplComponent,
    YapePeruComponent,
    PseWalletComponent,
    WalletClaroPayComponent,
    CheckOrderStatusComponent,
    UserFormComponent,
    BancolombiaComponent,
    QrComponent,
    SubMenuComponent,
    SubMenuComponentGU,
    PaymentFromWebsiteComponent,
    LoaderBnplComponent,
    PeruPaymentServicesComponent,
    SplitPaymentComponent,
    InfoOrderLinkComponent,
    NativeDaviplataComponent,
    VoucherTemplateComponent,
    SinpeMovilComponent,
    ConsumerCreditComponent,
    AmountToLowAlertComponent,
    DeunaEcuadorComponent,
    PuntosColombiaComponent,
    PopupRedirectComponent,
    TransfiyaComponent,
    TransfiyaVoucherComponent,
    ApiPayInVoucherComponent,
    WhiteBrandVoucherComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    FontAwesomeModule,
    FormsModule,
    SharedModule,
    PipesModule,
    ReactiveFormsModule,
    ClipboardModule,
    RecaptchaModule,
    MaterialModule,
    CreditCardDirectivesModule,
    CoreModule,
    MatProgressBarModule,
    NgxPopperModule.forRoot({}),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => {
          return new TranslateHttpLoader(http);
        },
        deps: [HttpClient],
      },
    }),
    MatListModule,
  ],
  providers: [
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    JwtHelperService,
    {
      provide: ErrorStateMatcher,
      useClass: CustomErrorStateMatcher
    },
    DecimalPipe,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})

export class AppModule {
  constructor(library: FaIconLibrary, private readonly featureFlagService: PayvalidaFeatureFlagService) {
    library.addIconPacks(fas, far, fab);

    this.featureFlagService.getFeatureFlagValue(FLAGS_NAME.ENABLE_SENTRY_FF, FLAGS_NAME.ENABLE_SENTRY_FF).subscribe((response: boolean) => {
      if (response) {
        Sentry.init({
          dsn: environment.sentryDsn,
          environment: environment.sentryEnv,
          sendClientReports: false,
          beforeSend(event, _) {
            if (event.tags.custom) return event;

            console.error(event);

            return null;
          },
          defaultIntegrations: false,
        });
      }
    });
  }
}
